import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import get from 'lodash/fp/get';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';

import { margin, useClassnames } from '@lumapps/classnames';
import { get as getConstants } from '@lumapps/constants';
import { useDataAttributes } from '@lumapps/data-attributes';
import { useInfiniteScroll } from '@lumapps/lumx-hooks/hooks/useInfiniteScroll';
import { usePrevious } from '@lumapps/lumx-hooks/hooks/usePrevious';
import { EmptyState } from '@lumapps/lumx-states/components/EmptyState';
import { WarningState } from '@lumapps/lumx-states/components/WarningState';
import { mdiClose, mdiFolderOpenOutline, mdiImageBrokenVariant } from '@lumapps/lumx/icons';
import {
    Button,
    Dialog,
    Emphasis,
    FlexBox,
    Heading,
    IconButton,
    Orientation,
    Size,
    Toolbar,
} from '@lumapps/lumx/react';
import { DOCUMENT_V1_SORTING, MOVE_DOCUMENT_FOLDER_STATUS } from '@lumapps/medias-document/constants';
import {
    useMoveDocumentNavigation,
    type UseMoveDocumentNavigationParams,
} from '@lumapps/medias-document/hooks/useMoveDocumentNavigation';
import type { DocumentV1, MoveDocumentNavigationConfirm } from '@lumapps/medias-document/types';
import { DocumentBlock } from '@lumapps/medias/components/DocumentBlock';
import { FolderBlockSkeletons } from '@lumapps/medias/components/FolderBlock/FolderBlockSkeletons';
import { MEDIAS } from '@lumapps/medias/keys';
import { PickerList } from '@lumapps/pickers/components/PickerList';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { FolderPickerDialogBreadcrumb } from './FolderPickerDialogBreadcrumb';

import './index.scss';

export interface FolderPickerDialogProps {
    /**
     * Initial data of the file/folder
     */
    initialDocument: DocumentV1 | null;
    /**
     * The initial sort order to apply when the dialog opens.
     */
    initialSort?: UseMoveDocumentNavigationParams['sortOrder'];
    /**
     * Indicates if the dialog should be in a loading state.
     * This can be used to show a loading spinner during an external process controlled by the parent component.
     * @default false
     */
    isDialogLoading?: boolean;
    /**
     * Controls if the dialog is open or closed.
     */
    isOpen: boolean;
    /**
     * Callback function executed when the user clicks the cancel button or closes the dialog.
     */
    onCancel: () => void;
    /**
     * Callback function executed when the user confirms the selection.
     */
    onConfirm: (data: MoveDocumentNavigationConfirm) => void;
}

const CLASSNAME = 'folder-picker-dialog';

/**
 * Renders a dialog that allows users to select a destination folder for moving a file or folder.
 *
 * @param {FolderPickerDialogProps}
 * @returns FolderPickerDialog
 */
export const FolderPickerDialog: React.FC<FolderPickerDialogProps> = ({
    initialDocument,
    initialSort = DOCUMENT_V1_SORTING.CREATED_DESC,
    isDialogLoading,
    isOpen,
    onCancel,
    onConfirm,
}) => {
    // Hooks
    const { translate, translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const { get: getDataAttributes } = useDataAttributes('picker-dialog');

    const { isLegacyContext } = getConstants();

    // Navigation and data fetching logic
    const {
        folderState,
        isError,
        isFetchingNextPage,
        isInitialFolder,
        isLoading,
        items,
        fetchNextPage,
        navigateToFolder,
        navigateToParent,
    } = useMoveDocumentNavigation({
        initialDocument,
        isEnabled: isOpen,
        sortOrder: initialSort,
        model: 'Folder',
    });

    // Callbacks
    const handleCancel = useCallback(() => onCancel(), [onCancel]);
    const handleConfirm = useCallback(() => {
        if (folderState.status !== MOVE_DOCUMENT_FOLDER_STATUS.LOADING) {
            onConfirm(folderState);
        }
    }, [folderState, onConfirm]);

    // Infinite scroll setup
    const contentRef = useRef(null);
    const isLoadingMoreRef = usePrevious(isFetchingNextPage);
    useInfiniteScroll(contentRef, !items?.length || isLoading || !fetchNextPage ? undefined : fetchNextPage);

    // Unique ID for ARIA attributes
    const labelId = useMemo(() => uniqueId(`${CLASSNAME}-label-`), []);

    // Logic to reset the list on data change
    const resetListOnItemsChange = !isLoadingMoreRef;
    const listKey = useRef('');
    useEffect(() => {
        if (items && resetListOnItemsChange) {
            listKey.current = uniqueId();
        }
    }, [items, resetListOnItemsChange]);

    // State calculation
    const isListLoading = isLoading || isFetchingNextPage;
    const hasNoResult = items.length === 0 && !isListLoading && !isError;
    const finalItems = isLoading && !isFetchingNextPage ? [] : items;
    const afterList = isListLoading ? FolderBlockSkeletons : undefined;

    // Item rendering
    const itemRenderer = useCallback((item: DocumentV1) => <DocumentBlock document={item} />, []);
    const disabledItemsRender = useMemo(
        () => items.filter((item) => !item.isFolder || item.id === initialDocument?.id),
        [items, initialDocument],
    );
    const itemNavigableSelector = useCallback((item: DocumentV1) => item.isFolder, []);

    return (
        <Dialog
            className={block()}
            /** The legacy picker have too many overlaping dialog causing it to disapear behind so we need to increase the z-index
             * This is due to the auto-increment in the legacy not transfering to react
             */
            zIndex={isLegacyContext ? 2000 : undefined}
            dialogProps={{ 'aria-labelledby': labelId }}
            forceHeaderDivider
            contentRef={contentRef}
            isOpen={isOpen}
            isLoading={(isLoading && !isFetchingNextPage) || isDialogLoading}
            size={Size.regular}
            onClose={handleCancel}
        >
            <header>
                <Toolbar
                    label={
                        <Heading as="h1" typography="title" id={labelId}>
                            {translateKey(GLOBAL.SELECT_FOLDER)}
                        </Heading>
                    }
                    after={
                        <IconButton
                            {...getDataAttributes({ element: 'button', action: 'close' })}
                            emphasis={Emphasis.low}
                            icon={mdiClose}
                            onClick={handleCancel}
                            label={translateKey(GLOBAL.CLOSE)}
                        />
                    }
                />

                <FolderPickerDialogBreadcrumb
                    folderState={folderState}
                    isInitialFolder={isInitialFolder}
                    navigateToParent={navigateToParent}
                />
            </header>

            {isError && (
                <WarningState
                    icon={mdiImageBrokenVariant}
                    title={translateKey(GLOBAL.GENERIC_ERROR)}
                    className={margin('vertical', 'huge')}
                />
            )}

            {hasNoResult && (
                <EmptyState
                    title={translateKey(MEDIAS.EMPTY_FOLDER)}
                    isVerticallyCentered={false}
                    iconProps={{
                        icon: mdiFolderOpenOutline,
                        size: Size.xxl,
                        alt: 'media-icon',
                        hasShape: true,
                        className: margin('vertical', 'huge'),
                    }}
                />
            )}

            {Boolean(!isError && (finalItems.length || isListLoading)) && (
                <PickerList<DocumentV1>
                    key={listKey.current}
                    aria-labelledby={labelId}
                    items={finalItems}
                    itemRenderer={itemRenderer}
                    itemKeySelector={get('id')}
                    itemNavigableSelector={itemNavigableSelector}
                    onNavigate={navigateToFolder}
                    afterList={afterList}
                    selectedItem={undefined}
                    setSelectedItem={noop}
                    disabledItems={disabledItemsRender}
                    itemSelectableSelector={(items) => items.isFolder}
                />
            )}

            <footer>
                <Toolbar
                    after={
                        <FlexBox orientation={Orientation.horizontal} gap="regular">
                            <Button
                                emphasis={Emphasis.medium}
                                {...getDataAttributes({ element: 'button', action: 'cancel' })}
                                className={element('cancel-button')}
                                onClick={handleCancel}
                            >
                                {translateKey(GLOBAL.CANCEL)}
                            </Button>
                            <Button
                                {...getDataAttributes({ element: 'button', action: 'confirm' })}
                                className={element('confirm-button')}
                                onClick={handleConfirm}
                                isDisabled={isInitialFolder}
                            >
                                {translate(GLOBAL.MOVE_HERE)}
                            </Button>
                        </FlexBox>
                    }
                />
            </footer>
        </Dialog>
    );
};
